import { useEffect } from "react";
import { useParams, useNavigate, Outlet, useLocation } from "react-router-dom";
import { Loading } from "@core/components";
import { useGetWorkspace, useGetWorkspaceForMembers } from "../hooks";
import { WorkspaceContext } from "@core/context";
import { SubscriptionProvider } from "@modules/subscription";

export const WorkspaceGuard = () => {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const path = location.pathname;

  const isAdminPath = path.includes("/console");
  const isMemberPath = path.includes("/project-portal");

  const adminQuery = useGetWorkspace(workspaceId ?? "", {
    enabled: !!workspaceId && isAdminPath,
  });

  const memberQuery = useGetWorkspaceForMembers(workspaceId ?? "", {
    enabled: !!workspaceId && isMemberPath,
  });

  const {
    data: workspace,
    isLoading,
    isError,
    error,
  } = isAdminPath ? adminQuery : memberQuery;

  /**
   * =========================
   * ROUTE GUARD LOGIC
   * =========================
   */
  useEffect(() => {
    if (!workspaceId) {
      navigate("/select-workspace", { replace: true });
      return;
    }

    // Handle API errors via redirects only
    if (isError) {
      const status = (error as any)?.response?.status;

      if (status === 401) {
        navigate("/login", { replace: true });
        return;
      }

      // forbidden / not in workspace
      if (status === 403) {
        navigate("/select-workspace", { replace: true });
        return;
      }

      // workspace not found
      if (status === 404) {
        navigate("/select-workspace", { replace: true });
        return;
      }

      navigate("/select-workspace", { replace: true });
      return;
    }

    if (!workspace) return;

    // Role-based routing correction
    if (isAdminPath && path.includes("/project-portal")) {
      navigate(`/workspace/${workspaceId}/console/members`, {
        replace: true,
      });
      return;
    }

    if (
      isMemberPath &&
      (path.includes("/console") || path === `/workspace/${workspaceId}`)
    ) {
      navigate(`/workspace/${workspaceId}/project-portal`, {
        replace: true,
      });
      return;
    }
  }, [
    workspace,
    workspaceId,
    isError,
    error,
    navigate,
    isAdminPath,
    isMemberPath,
    path,
  ]);

  /**
   * =========================
   * LOADING STATE
   * =========================
   */
  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white">
        <Loading isLoading />
      </div>
    );
  }

  /**
   * =========================
   * WORKSPACE NOT READY
   * (avoid UI errors — let redirect handle it)
   * =========================
   */
  if (!workspace) return null;

  /**
   * =========================
   * WORKSPACE LIFECYCLE BLOCKS
   * =========================
   */

  if (workspace.status === "suspended") {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white">
        <p className="text-sm text-neutral-500">Workspace suspended</p>
      </div>
    );
  }

  if (workspace.status === "pending") {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white">
        <p className="text-sm text-neutral-500">
          Workspace pending — admin has not accepted invite yet
        </p>
      </div>
    );
  }

  if (workspace.status === "archived") {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white">
        <p className="text-sm text-neutral-500">Workspace not found</p>
      </div>
    );
  }

  if (workspace.is_suspended) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white">
        <p className="text-sm text-neutral-500">
          Your access to this workspace has been suspended
        </p>
      </div>
    );
  }

  /**
   * =========================
   * PROVIDER WRAP
   * =========================
   */
  return (
    <WorkspaceContext.Provider
      value={{
        workspace,
        role: isAdminPath ? "admin" : "member",
        workspaceId: workspaceId ?? "",
      }}
    >
      <SubscriptionProvider>
        <Outlet />
      </SubscriptionProvider>
    </WorkspaceContext.Provider>
  );
};
